// One contact in a campaign: what they'll actually receive (master + their merge values + overrides), with per-contact
// edits ("customized"), merge-value fixes, the CRM context panel, schedule and activity.
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, useApi } from "../lib/api";
import { Badge, Button, Card, ErrorNote, Field, fieldLabel, fmtDate, fmtDay, inputCls, Loading, Menu, Modal, Notice, PageHeader, StickyBar, useAction } from "../components/ui";
import { buildSteps, SequenceTimeline, SignatureNote, type Step } from "../components/Sequence";

export default function Review() {
  const { cid, ct } = useParams();
  const { data, error, loading, reload } = useApi<any>(`/enrollments/${cid}/${ct}`, [cid, ct]);
  const act = useAction();
  const [editMerge, setEditMerge] = useState(false);
  const [vals, setVals] = useState<any>({});
  if (loading && !data) return <Loading rows={10} />;
  if (!data) return <ErrorNote error={error} />;
  const e = data.enrollment;
  const c = data.contact;
  const r = data.rendered;
  const sentLabels = new Set((data.steps ?? []).filter((s: any) => s.status === "sent" || s.status === "done").map((s: any) => s.label));
  const live = ["Enrolled", "Active", "Paused"].includes(e.status);
  const overrides = data.overrides ?? {};
  const isOverridden = (key: string) => {
    const [kind, n] = key.split(":");
    return kind === "email" ? !!overrides.emails?.[n!] : kind === "call" ? !!overrides.callScripts?.[n!] : key === "linkedin_note" ? overrides.linkedinNote !== undefined : overrides.linkedinMessage !== undefined;
  };
  const findings = [...(r?.leak?.blocks ?? []), ...(r?.leak?.warnings ?? [])];
  const steps: Step[] = r
    ? buildSteps(data.template, { emails: r.emails.map((m: any) => ({ subject: m.subject, body: m.core })), callScripts: r.callScripts, linkedinNote: r.linkedinNote, linkedinMessage: r.linkedinMessage }, findings, {
        editable: (key) => live && !sentLabels.has(labelOf(key)),
        badge: (key) => (isOverridden(key) ? "customized" : undefined),
        sent: (label) => sentLabels.has(label),
      })
    : [];
  const saveOverride = async (step: Step, value: { subject?: string; body: string }, secs: number) => {
    await api(`/enrollments/${cid}/${ct}/override`, { method: "PUT", body: { piece: step.key, subject: value.subject, body: value.body, editSeconds: secs } });
    await reload();
  };
  const simple = (path: string, body: any = {}) => act.run(async () => { await api(`/enrollments/${cid}/${ct}/${path}`, { body }); reload(); });
  const canApprove = live && e.status === "Enrolled" && e.draftStatus !== "approved" && !data.approvalBlocker;

  return (
    <div className="space-y-5">
      <PageHeader
        title={<Link className="hover:underline" to={`/contacts/${c.id}`}>{c.firstName} {c.lastName}</Link>}
        sub={
          <span className="flex flex-wrap items-center gap-2">
            {c.title} · {data.brief?.name} · {c.state ?? "no state"} ·
            <Link className="text-accent-text hover:underline" to={`/campaigns/${cid}`}>{data.campaign?.name}</Link>
            <Badge tone={e.status === "Enrolled" ? (e.draftStatus === "approved" ? "approved" : "pending") : e.status}>{e.status === "Enrolled" ? (e.draftStatus === "approved" ? "Approved" : "Waiting for approval") : e.status}</Badge>
          </span>
        }
        actions={
          live && (
            <Menu
              items={[
                e.status === "Paused" ? { label: "Resume (keep)", onClick: () => simple("resume") } : { label: "Pause", onClick: () => simple("pause", { reason: "Paused by rep" }), disabled: e.status !== "Active" },
                { label: "Remove from campaign", danger: true, onClick: () => simple("remove", { reason: "removed by rep" }) },
              ]}
            />
          )
        }
      />
      <ErrorNote error={act.error} />
      {e.pausedReason && <Notice>{e.status}: {e.pausedReason}</Notice>}
      {data.review?.kind === "held" && <Notice tone="warn">Held back: {data.review.text}. Fix the value below; nothing is sent to this contact until then.</Notice>}
      {!r && <Notice>This contact has no rendered sequence yet (the campaign is still being drafted).</Notice>}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-5">
          {r && (
            <Card title="What this contact receives" actions={<span className="text-xs text-muted">Editing a step customizes it for this contact only</span>}>
              <SequenceTimeline
                steps={steps}
                threadSubject={r.emails[0]?.subject}
                onSave={(s, v, secs) => saveOverride({ ...s }, v, secs)}
                footer={<SignatureNote signer={r.signer} postal={data.postalAddress} />}
              />
              {Object.keys(overrides.emails ?? {}).length + Object.keys(overrides.callScripts ?? {}).length + (overrides.linkedinNote !== undefined ? 1 : 0) + (overrides.linkedinMessage !== undefined ? 1 : 0) > 0 && (
                <div className="mt-3 flex flex-wrap gap-2 border-t border-line pt-3 text-xs">
                  <span className="text-muted">Customized:</span>
                  {steps.filter((s) => isOverridden(s.key)).map((s) => (
                    <Button key={s.key} tone="ghost" onClick={() => act.run(async () => { await api(`/enrollments/${cid}/${ct}/override`, { method: "PUT", body: { piece: s.key, clear: true } }); reload(); })}>
                      Put {s.label} back to the campaign version
                    </Button>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
        <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          <Card title="Merge values" actions={live && <Button tone="ghost" onClick={() => { setVals({ ...e.merge }); setEditMerge(true); }}>Edit</Button>}>
            <dl className="space-y-1.5 text-sm">
              {(["first_name", "company", "title", "signal_line"] as const).map((k) => (
                <div key={k} className="grid grid-cols-[96px_minmax(0,1fr)] gap-2">
                  <dt className="text-muted">{`{${k}}`}</dt>
                  <dd className="text-ink">{e.merge?.[k] ? String(e.merge[k]).replace("{company}", e.merge.company) : <span className="text-muted">empty</span>}</dd>
                </div>
              ))}
            </dl>
          </Card>
          <Brief brief={data.brief} signals={data.signals} />
          {(data.steps ?? []).length > 0 && (
            <Card title="Schedule">
              <ul className="space-y-1 text-sm">
                {data.steps.map((s: any) => (
                  <li key={s.n} className="flex items-center justify-between gap-2">
                    <span className="text-ink-2">{s.label}</span>
                    <span className="flex items-center gap-1 text-xs text-muted">{s.type === "email" ? fmtDate(s.sentAt ?? s.plannedAt) : fmtDay(s.plannedAt)} <Badge>{s.status}</Badge></span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          <Card title="Activity">
            <ul className="max-h-80 space-y-1 overflow-auto text-xs">
              {(data.events ?? []).map((ev: any) => (
                <li key={ev.id} className="text-ink-2"><span className="text-muted">{fmtDate(ev.at)}</span> · <span className="font-medium">{ev.type}</span> {ev.detail}</li>
              ))}
            </ul>
          </Card>
        </aside>
      </div>

      {canApprove && (
        <StickyBar>
          <span className="mr-auto text-sm text-muted">{data.review ? `Needs a look: ${data.review.text}` : ""}</span>
          <Button tone="primary" disabled={act.busy} onClick={() => simple("approve")}>Approve</Button>
        </StickyBar>
      )}
      {!canApprove && live && e.status === "Enrolled" && e.draftStatus !== "approved" && data.approvalBlocker && (
        <StickyBar>
          <span className="text-sm text-muted">Approve isn't available: {data.approvalBlocker}</span>
        </StickyBar>
      )}

      {editMerge && (
        <Modal title="Merge values for this contact" onClose={() => setEditMerge(false)} actions={<><Button onClick={() => setEditMerge(false)}>Cancel</Button><Button tone="primary" disabled={act.busy} onClick={() => act.run(async () => { await api(`/enrollments/${cid}/${ct}/merge`, { method: "PUT", body: vals }); setEditMerge(false); reload(); })}>Save</Button></>}>
          <Field label="First name"><input className={inputCls} value={vals.first_name ?? ""} onChange={(ev) => setVals({ ...vals, first_name: ev.target.value })} /></Field>
          <Field label="Company (as written in the email)"><input className={inputCls} value={vals.company ?? ""} onChange={(ev) => setVals({ ...vals, company: ev.target.value })} /></Field>
          <Field label="Title"><input className={inputCls} value={vals.title ?? ""} onChange={(ev) => setVals({ ...vals, title: ev.target.value })} /></Field>
          <Field label="Signal line (empty for none)" help="{company} is replaced with the company name."><textarea className={inputCls} rows={2} value={vals.signal_line ?? ""} onChange={(ev) => setVals({ ...vals, signal_line: ev.target.value })} /></Field>
          <ErrorNote error={act.error} />
        </Modal>
      )}
    </div>
  );
}

const labelOf = (key: string) => {
  const [kind, n] = key.split(":");
  return kind === "email" ? `Email ${Number(n) + 1}` : kind === "call" ? `Call ${Number(n) + 1}` : key === "linkedin_note" ? "LinkedIn connect" : "LinkedIn message";
};

function Brief({ brief, signals }: { brief: any; signals: any[] }) {
  if (!brief) return null;
  const on = (o: Record<string, boolean>) => Object.entries(o ?? {}).filter(([, v]) => v).map(([k]) => fieldLabel(k));
  const rows: [string, any][] = [
    ["Industry", brief.industry],
    ["Tier / play", [brief.tier && `Tier ${brief.tier}`, brief.play].filter(Boolean).join(" · ")],
    ["Fit", on(brief.fit).join(", ")],
    ["Licensed", on(brief.licensed).join(", ")],
    ["Territory owner", brief.territoryOwner],
    ["Intellias relationship", brief.relationship],
    ["Open opps", brief.openOpps],
    ["Closed-won", brief.closedWon],
  ];
  return (
    <Card title="CRM context (never in emails)">
      <dl className="space-y-1.5 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3"><dt className="text-muted">{k}</dt><dd className="text-right text-ink-2">{v || "—"}</dd></div>
        ))}
      </dl>
      {signals.length > 0 && (
        <div className="mt-3 border-t border-line pt-2 text-xs">
          <div className="mb-1 font-medium text-muted">Public signals</div>
          {signals.map((s) => (
            <div key={s.id} className="text-ink-2">{s.type === "rfp" ? `RFP ${s.agency ?? ""} ${s.scope ?? ""} (due ${s.dueDate ?? "?"})` : `Job posting: ${s.jobTitle} (posted ${s.postedDate ?? "?"})`}{!s.fresh && " · expired"}</div>
          ))}
        </div>
      )}
    </Card>
  );
}
